// ============================================================================
// L'ÉDITEUR DE COMMANDES ET DE BL, SANS L'ÉCRAN — la grille d'un modèle, les quantités, les conditions et le lot.
//
// UTILITÉ : tout ce que la modale calcule vit ici, en fonctions pures, pour être éprouvé sans monter
// l'écran. L'écran n'en est que le rendu.
//
// ⚠️ LES MONTANTS VIENNENT DU CŒUR (`purchaseOrderTotals`) : le même calcul que le serveur et que le bon de
//    commande. Un écran qui ferait son propre net afficherait un centime de plus que le document.
// ⚠️ LES QUANTITÉS SONT TENUES PAR CODE-BARRES : une case de la grille EST une déclinaison, et c'est ce
//    que la commande enregistre — ligne par ligne, comme le BL la recevra.
// ============================================================================
import { purchaseOrderTotals, type PurchaseOrderTotals } from '@orgazone/core-inventory';
import { formatRateBp, parseRateBp } from '@orgazone/core-vat';
import { parseMinor, toInputValue } from '@orgazone/money-format';
import { DOCUMENT_NUMBER_FALLBACK, documentNumberRefusal, proposeDocumentNumber, supplierTermsFieldRefusal, type CivilDay } from '@orgazone/sync-contract';
import type { DeliveryGroupInput, OrderBrand, OrderGroupInput, OrderModel, SupplierTerms } from '../api';

/**
 * Ce que l'éditeur saisit : une commande (la promesse), ou un BL (le réel reçu).
 * ⚠️ UN SEUL ÉDITEUR POUR LES DEUX : la grille, les groupes par fournisseur, le numéro et la
 *    date sont les mêmes — seuls changent les conditions (une réception n'en porte pas) et ce que le lot écrit.
 */
export type EditorKind = 'order' | 'deliveryNote';

/** Le coloris d'une déclinaison qui n'en déclare pas — une ligne de grille quand même. */
export const NO_COLOR = '—';

/** Un article dans la saisie : son modèle, son prix d'achat tapé, et ses quantités par code-barres. */
export interface EditorCard {
  model: OrderModel;
  /** Le prix d'achat HT tel que saisi (« 28,40 ») — lu à l'échelle fine au moment d'enregistrer. */
  pa: string;
  qty: Record<string, number>;
}

/** Les conditions d'une commande — ou le numéro et la date d'un BL —, telles que saisies dans le panneau de droite. */
export interface EditorConditions {
  /**
   * Le numéro TAPÉ — `null` tant que l'opérateur garde la proposition, qui suit alors les autres fournisseurs
   * de la saisie. Un brouillon repris porte le sien.
   */
  number: string | null;
  /** La date de commande — ou du BL —, jour civil `AAAA-MM-JJ` ; le jour même par défaut. */
  orderedOn: string;
  discount: string;
  shipping: string;
  earlyPayment: string;
  earlyPaymentDays: string;
  /** Le brouillon que cette commande reprend — `null` pour une commande neuve. */
  draftNumber: string | null;
  /** La version du brouillon lue à son ouverture — le serveur refuse d'écraser une saisie plus récente. */
  draftUpdatedAt: number | null;
}

/** Une ligne de la grille : un coloris (× une valeur du second axe), une case par taille — `null` = pas de déclinaison. */
export interface GridRow {
  color: string;
  size2: string | null;
  /** Premier rang de son coloris — c'est lui qui porte le nom du coloris. */
  first: boolean;
  cells: (string | null)[];
}

export interface Grid {
  sizes: string[];
  rows: GridRow[];
  /** Le libellé du second axe (« Longueur ») — `null` sans second axe. */
  axisLabel: string | null;
}

/**
 * La grille d'un modèle : les tailles dans l'ordre de la GRILLE, une ligne par coloris (× second axe),
 * une case par déclinaison existante.
 * ⚠️ L'ORDRE DES TAILLES EST CELUI DE LA GRILLE, jamais l'alphabet (L avant M, S avant XS). Sans grille,
 *    l'ordre d'apparition — l'écran ne prétend pas alors que c'est un ordre de tailles.
 * ⚠️ UNE CASE ABSENTE RESTE VISIBLE (pointillé) : la faire disparaître décalerait les colonnes et ferait
 *    taper une quantité dans la mauvaise taille.
 */
export function gridOf(model: OrderModel): Grid {
  const sizeOf = (s: string | null) => s ?? '';
  const sizes = [...model.sizeOrder];
  for (const c of model.cells) if (!sizes.includes(sizeOf(c.size))) sizes.push(sizeOf(c.size));
  const colors: string[] = [];
  for (const c of model.cells) { const k = c.color ?? NO_COLOR; if (!colors.includes(k)) colors.push(k); }
  const seconds: (string | null)[] = [...(model.dimension?.values ?? [])];
  for (const c of model.cells) if (c.size2 && !seconds.includes(c.size2)) seconds.push(c.size2);
  if (seconds.length === 0) seconds.push(null);

  const byKey = new Map(model.cells.map((c) => [`${c.color ?? NO_COLOR}|${sizeOf(c.size)}|${c.size2 ?? ''}`, c.gencode]));
  const rows: GridRow[] = [];
  for (const color of colors) {
    seconds.forEach((size2, i) => {
      rows.push({ color, size2, first: i === 0, cells: sizes.map((s) => byKey.get(`${color}|${s}|${size2 ?? ''}`) ?? null) });
    });
  }
  return { sizes, rows, axisLabel: model.dimension?.label ?? null };
}

/** Les pièces d'un article — la somme de sa grille. */
export const cardPieces = (card: EditorCard): number => Object.values(card.qty).reduce((t, n) => t + n, 0);

/** Le total d'une ligne de grille. */
export const rowTotal = (card: EditorCard, row: GridRow): number =>
  row.cells.reduce((t, g) => t + (g ? card.qty[g] ?? 0 : 0), 0);

/** Les totaux par colonne de taille. */
export const columnTotals = (card: EditorCard, grid: Grid): number[] =>
  grid.sizes.map((_, j) => grid.rows.reduce((t, r) => t + (r.cells[j] ? card.qty[r.cells[j]!] ?? 0 : 0), 0));

/** « 1 partout » : une pièce dans chaque case qui existe. */
export function fillAll(grid: Grid): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of grid.rows) for (const g of r.cells) if (g) out[g] = 1;
  return out;
}

/**
 * Dupliquer une ligne vers les autres coloris, à valeur égale du second axe.
 * Rend les nouvelles quantités et le nombre de cases qui portaient DÉJÀ une autre quantité — l'écran
 * demande alors confirmation avant de les remplacer.
 */
export function duplicateRow(card: EditorCard, grid: Grid, rowIndex: number): { qty: Record<string, number>; overwritten: number; targets: number } {
  const src = grid.rows[rowIndex]!;
  const qty = { ...card.qty };
  let overwritten = 0;
  const others = grid.rows.filter((r) => r.color !== src.color && r.size2 === src.size2);
  for (const r of others) {
    r.cells.forEach((g, j) => {
      const from = src.cells[j];
      if (!g || !from) return;
      const v = card.qty[from] ?? 0;
      const before = card.qty[g] ?? 0;
      if (before > 0 && before !== v) overwritten += 1;
      if (v > 0) qty[g] = v; else delete qty[g];
    });
  }
  return { qty, overwritten, targets: new Set(others.map((r) => r.color)).size };
}

/** Une quantité tapée dans une case : chiffres seulement, `0` ou vide = rien. */
export function readQty(v: string): number | null {
  const t = v.trim();
  if (t === '') return 0;
  return /^\d{1,6}$/.test(t) ? Number(t) : null;
}

/** Le prix tapé, à l'échelle fine (4 décimales) — `null` si vide, `undefined` s'il est illisible. */
export function fineFromInput(v: string): number | null | undefined {
  const p = parseMinor(v, { scale: 4 });
  if (p.state === 'empty') return null;
  return p.state === 'ok' && p.minor >= 0 ? p.minor : undefined;
}

/** Un prix fin dans un champ : deux décimales au moins, quatre au plus (« 28,40 », « 9,7403 »). */
export function inputFromFine(fine: number | null): string {
  if (fine === null) return '';
  const full = toInputValue(fine, 4);
  return full.replace(/(,\d\d)(\d*?)0+$/, (_, head: string, tail: string) => head + tail);
}

/** Un jour civil `AAAA-MM-JJ`. */
const CIVIL_DATE = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** Le jour civil d'un instant, lu dans le fuseau du NAVIGATEUR — celui où l'opérateur voit « aujourd'hui ». */
export function civilOf(t: number): string {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Aujourd'hui, en jour civil. */
export const todayCivil = (): string => civilOf(Date.now());

/**
 * L'instant envoyé au serveur pour un jour civil : son minuit LOCAL.
 * ⚠️ Le minuit, et non l'heure de la saisie : le serveur juge « jamais dans le futur » par `orderedOn <= now`,
 *    vrai pour le minuit du jour quel que soit le fuseau, faux pour celui du lendemain.
 */
export function localMidnight(civil: string): number {
  const m = CIVIL_DATE.exec(civil);
  if (!m) return Number.NaN;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  // ⚠️ Un 31 février « se lit » en 3 mars : un jour qui n'existe pas est refusé, jamais déplacé.
  return d.getDate() === Number(m[3]) ? d.getTime() : Number.NaN;
}

const dayOf = (civil: string): CivilDay => {
  const [y, m, d] = civil.split('-').map(Number);
  return { year: y!, month: m!, day: d! };
};

/** Pourquoi une date de commande ne part pas — `null` si elle part. Deux jours civils se comparent comme des textes : même forme, même ordre. */
export function orderDateProblem(civil: string, today: string): 'missing' | 'future' | null {
  if (Number.isNaN(localMidnight(civil))) return 'missing';
  return civil > today ? 'future' : null;
}

/** Les conditions recopiées de la fiche fournisseur, prêtes à être modifiées pour cette commande. */
export function conditionsFromTerms(
  terms: SupplierTerms | null, draftNumber: string | null = null, draftUpdatedAt: number | null = null, orderedOn: string = todayCivil(),
): EditorConditions {
  return {
    number: draftNumber,
    orderedOn,
    discount: terms?.discountBp != null ? formatRateBp(terms.discountBp, false) : '',
    shipping: terms?.shippingCents != null ? toInputValue(terms.shippingCents, 2) : '',
    earlyPayment: terms?.earlyPaymentBp != null ? formatRateBp(terms.earlyPaymentBp, false) : '',
    earlyPaymentDays: terms?.earlyPaymentDays != null ? String(terms.earlyPaymentDays) : '',
    draftNumber,
    draftUpdatedAt,
  };
}

/** Les conditions lues — ou le champ qui ne se lit pas. */
export type ReadConditions =
  | { ok: true; discountBp: number | null; shippingCents: number | null; earlyPaymentBp: number | null; earlyPaymentDays: number | null }
  | { ok: false; field: 'discount' | 'shipping' | 'earlyPayment' | 'earlyPaymentDays' };

/**
 * Lit les conditions saisies. ⚠️ REFUSER, JAMAIS CORRIGER : une remise « 2,555 » n'est pas arrondie à
 * 2,56 — elle est refusée, et l'écran dit quel champ corriger.
 */
export function readConditions(c: EditorConditions): ReadConditions {
  const rate = (v: string): number | null | undefined => (v.trim() === '' ? null : parseRateBp(v) ?? undefined);
  const discountBp = rate(c.discount);
  if (discountBp === undefined) return { ok: false, field: 'discount' };
  const earlyPaymentBp = rate(c.earlyPayment);
  if (earlyPaymentBp === undefined) return { ok: false, field: 'earlyPayment' };
  const ship = parseMinor(c.shipping, { scale: 2 });
  if (ship.state === 'rejected' || (ship.state === 'ok' && ship.minor < 0)) return { ok: false, field: 'shipping' };
  const days = c.earlyPaymentDays.trim();
  if (days !== '' && !/^\d{1,3}$/.test(days)) return { ok: false, field: 'earlyPaymentDays' };
  const shippingCents = ship.state === 'ok' ? ship.minor : null;
  const earlyPaymentDays = days === '' ? null : Number(days);
  // ⚠️ LES BORNES SONT CELLES DE LA FICHE FOURNISSEUR, lues dans la MÊME règle que le serveur : un délai de
  //    0 jour ou un port démesuré annoncés à l'écran puis refusés à l'envoi seraient deux règles du même fait.
  if (supplierTermsFieldRefusal('discountBp', discountBp)) return { ok: false, field: 'discount' };
  if (supplierTermsFieldRefusal('shippingCents', shippingCents)) return { ok: false, field: 'shipping' };
  if (supplierTermsFieldRefusal('earlyPaymentBp', earlyPaymentBp)) return { ok: false, field: 'earlyPayment' };
  if (supplierTermsFieldRefusal('earlyPaymentDays', earlyPaymentDays)) return { ok: false, field: 'earlyPaymentDays' };
  return { ok: true, discountBp, shippingCents, earlyPaymentBp, earlyPaymentDays };
}

/**
 * Un groupe de la saisie : un fournisseur, ou une marque que rien ne rattache encore à un fournisseur.
 * ⚠️ `key` est l'identité du groupe — dans les conditions, les numéros et l'écran : l'identifiant du
 *    fournisseur, ou `brand:<id>`. Jamais un fournisseur vide, sous lequel deux marques se confondraient.
 */
export interface EditorGroup {
  key: string;
  /** `null` tant que la marque n'est rattachée à aucun fournisseur. */
  supplierId: string | null;
  /** Le fournisseur — ou la marque, quand il reste à définir : c'est le mot qui nomme la commande. */
  supplierLabel: string;
  /** La marque à rattacher — renseignée seulement quand le fournisseur reste à définir. */
  orphanBrand: OrderBrand | null;
  cards: number[];
}

/** L'identité du groupe d'un modèle — `null` sans fournisseur NI marque : il n'y a rien à rattacher (D4). */
export function groupKeyOf(m: OrderModel): string | null {
  if (m.supplier) return m.supplier.id;
  return m.brandRef ? `brand:${m.brandRef.id}` : null;
}

/** Les groupes de la saisie, dans l'ordre de leur premier article, avec leurs articles. */
export function groupsOf(cards: readonly EditorCard[]): EditorGroup[] {
  const out: EditorGroup[] = [];
  cards.forEach((c, i) => {
    const key = groupKeyOf(c.model);
    if (!key) return;
    const g = out.find((x) => x.key === key);
    if (g) { g.cards.push(i); return; }
    const s = c.model.supplier;
    out.push(s
      ? { key, supplierId: s.id, supplierLabel: s.label, orphanBrand: null, cards: [i] }
      : { key, supplierId: null, supplierLabel: c.model.brandRef!.label, orphanBrand: c.model.brandRef, cards: [i] });
  });
  return out;
}

/** Les fournisseurs qui portent au moins une pièce — ceux qui partiront, donc ceux qui ont un numéro. */
export const filledGroups = (cards: readonly EditorCard[]) => groupsOf(cards).filter((g) => g.cards.some((i) => cardPieces(cards[i]!) > 0));

/** L'adresse de commande d'un fournisseur de la saisie — celle de sa fiche ; `null` sans adresse ou pour une marque à rattacher. */
export function orderEmailOf(cards: readonly EditorCard[], key: string): string | null {
  const c = cards.find((x) => groupKeyOf(x.model) === key);
  return c?.model.supplier?.terms.orderEmail?.trim() || null;
}

/**
 * CE QUE LA VALIDATION FERA — combien de commandes partiront, et combien seront ENVOYÉES par e-mail.
 * ⚠️ UNE COMMANDE N'EST ENVOYÉE QUE SI LA MESSAGERIE DU MAGASIN EST RÉGLÉE ET QUE SA FICHE PORTE UNE ADRESSE : le
 *    bouton ne promet pas un envoi qui n'aura pas lieu (D1). Une marque à rattacher ne part pas du tout.
 */
export function sendPlan(cards: readonly EditorCard[], mailReady: boolean): { valid: number; sendable: number } {
  const groups = filledGroups(cards).filter((g) => !g.orphanBrand);
  return { valid: groups.length, sendable: mailReady ? groups.filter((g) => orderEmailOf(cards, g.key) != null).length : 0 };
}

/** Les lignes d'un groupe : une par case remplie, au prix d'achat de son article. */
function linesOf(cards: readonly EditorCard[], idx: readonly number[]): { gencode: string; orderedQty: number; unitPriceFine: number | null }[] {
  const out: { gencode: string; orderedQty: number; unitPriceFine: number | null }[] = [];
  for (const i of idx) {
    const c = cards[i]!;
    const fine = fineFromInput(c.pa);
    for (const [gencode, q] of Object.entries(c.qty)) {
      if (q > 0) out.push({ gencode, orderedQty: q, unitPriceFine: typeof fine === 'number' ? fine : null });
    }
  }
  return out;
}

/**
 * Les montants d'un groupe — calculés par le CŒUR, avec les conditions saisies (illisibles = ignorées,
 * l'écran signale le champ fautif à côté).
 */
export function groupTotals(cards: readonly EditorCard[], idx: readonly number[], c: EditorConditions | undefined): PurchaseOrderTotals {
  const r = c ? readConditions(c) : null;
  const terms = r && r.ok ? { discountBp: r.discountBp, shippingCents: r.shippingCents, earlyPaymentBp: r.earlyPaymentBp } : {};
  return purchaseOrderTotals(linesOf(cards, idx).map((l) => ({ qty: l.orderedQty, unitPriceFine: l.unitPriceFine })), terms);
}

/** Un brouillon repris dans l'éditeur : son numéro et la version lue à son ouverture. */
export interface LoadedDraft {
  number: string;
  updatedAt: number | null;
}

/**
 * Les quantités qu'un modèle peut porter — celles de ses CASES — et le nombre de lignes écartées.
 * ⚠️ Une quantité sans case (déclinaison archivée, ou sans code-barres) serait INVISIBLE dans la grille et
 *    partirait pourtant au serveur : la carte afficherait un total qui ne correspond pas à ce qui est commandé.
 */
export function orderableQty(model: OrderModel, qty: Readonly<Record<string, number>>): { qty: Record<string, number>; dropped: number } {
  const cells = new Set(model.cells.map((c) => c.gencode));
  const out: Record<string, number> = {};
  let dropped = 0;
  for (const [gencode, n] of Object.entries(qty)) {
    if (cells.has(gencode)) out[gencode] = n; else dropped += 1;
  }
  return { qty: out, dropped };
}

/** Pourquoi le numéro d'une commande ne part pas. */
export type NumberProblem = 'empty' | 'bad' | 'taken' | 'duplicate';

/** Le numéro d'un fournisseur de la saisie, et ce qui l'empêche de partir. */
export interface GroupNumber { number: string; problem: NumberProblem | null }

/**
 * LES NUMÉROS DE LA SAISIE — tapé, ou proposé (`TEXTIL-140826`) en sautant ceux déjà en base ET ceux déjà
 * retenus par les fournisseurs précédents de la même saisie.
 * ⚠️ L'ÉCRAN CONSEILLE, LE SERVEUR JUGE : `taken` est la liste lue à l'ouverture, un autre poste a pu prendre
 *    le numéro depuis — le serveur le refusera alors en le nommant.
 * ⚠️ Un brouillon repris garde son numéro : présent dans `taken`, il n'est pas « pris » pour lui-même.
 */
export function numbersOf(
  groups: readonly { key: string; supplierLabel: string }[], conds: Readonly<Record<string, EditorConditions>>,
  taken: ReadonlySet<string>, today: string, kind: EditorKind = 'order',
): Map<string, GroupNumber> {
  const assigned: string[] = [];
  for (const g of groups) {
    const typed = conds[g.key]?.number;
    assigned.push(typed != null
      ? typed.trim().toUpperCase()
      : proposeDocumentNumber(g.supplierLabel, dayOf(today), (x) => taken.has(x) || assigned.includes(x), DOCUMENT_NUMBER_FALLBACK[kind]));
  }
  const out = new Map<string, GroupNumber>();
  groups.forEach((g, i) => {
    const n = assigned[i]!;
    const own = conds[g.key]?.draftNumber ?? null;
    const why = documentNumberRefusal(n);
    const problem: NumberProblem | null = why === 'empty' ? 'empty' : why ? 'bad'
      : taken.has(n) && n !== own ? 'taken'
        : assigned.filter((x) => x === n).length > 1 ? 'duplicate' : null;
    out.set(g.key, { number: n, problem });
  });
  return out;
}

/** Ce qui empêche d'enregistrer, nommé pour l'écran — vide si le lot peut partir. */
export type BatchProblem =
  | { kind: 'price'; model: string }
  | { kind: 'conditions'; supplier: string; field: string }
  | { kind: 'number'; supplier: string; problem: NumberProblem }
  | { kind: 'date'; supplier: string; problem: 'missing' | 'future' };

/**
 * LE LOT À ENVOYER — une commande par fournisseur qui porte au moins une pièce, et les brouillons que la
 * saisie a vidés.
 * ⚠️ UN PRIX ILLISIBLE BLOQUE L'ENVOI, il ne part pas « sans prix » : l'opérateur a tapé quelque chose,
 *    l'ignorer enregistrerait une commande différente de celle qu'il voit.
 */
export function batchOf(
  cards: readonly EditorCard[], conds: Readonly<Record<string, EditorConditions>>, loadedDrafts: readonly LoadedDraft[],
  taken: ReadonlySet<string>, today: string, mode: 'draft' | 'validate',
): { groups: OrderGroupInput[]; discardDrafts: LoadedDraft[]; problems: BatchProblem[]; held: string[] } {
  const problems: BatchProblem[] = [];
  const groups: OrderGroupInput[] = [];
  /** Les marques sans fournisseur qu'une validation laisse dans l'éditeur — nommées pour l'écran. */
  const held: string[] = [];
  const heldDrafts = new Set<string>();
  const all = groupsOf(cards);
  // ⚠️ LES NUMÉROS SE CALCULENT SUR LES FOURNISSEURS QUI PARTENT — ceux que l'écran montre avec un numéro :
  //    un fournisseur vidé de ses pièces ne retient pas un numéro qui en ferait changer un autre.
  const numbers = numbersOf(filledGroups(cards), conds, taken, today);
  for (const g of all) {
    const lines = linesOf(cards, g.cards);
    if (lines.length === 0) continue;
    const cond = conds[g.key] ?? conditionsFromTerms(null, null, null, today);
    // ⚠️ UNE MARQUE SANS FOURNISSEUR NE PART PAS À LA VALIDATION : elle reste dans l'éditeur, son brouillon
    //    avec elle — sans destinataire, la commande ne promettrait rien à personne. En brouillon, elle part.
    if (g.orphanBrand && mode === 'validate') {
      held.push(g.supplierLabel);
      if (cond.draftNumber) heldDrafts.add(cond.draftNumber);
      continue;
    }
    for (const i of g.cards) {
      const c = cards[i]!;
      if (cardPieces(c) > 0 && fineFromInput(c.pa) === undefined) problems.push({ kind: 'price', model: c.model.name });
    }
    const num = numbers.get(g.key)!;
    if (num.problem) problems.push({ kind: 'number', supplier: g.supplierLabel, problem: num.problem });
    const dateProblem = orderDateProblem(cond.orderedOn, today);
    if (dateProblem) problems.push({ kind: 'date', supplier: g.supplierLabel, problem: dateProblem });
    const r = readConditions(cond);
    if (!r.ok) { problems.push({ kind: 'conditions', supplier: g.supplierLabel, field: r.field }); continue; }
    groups.push({
      draftNumber: cond.draftNumber, draftUpdatedAt: cond.draftUpdatedAt, supplierId: g.supplierId ?? '', supplierLabel: g.supplierLabel,
      ...(g.orphanBrand ? { brandId: g.orphanBrand.id } : {}),
      number: num.number, orderedOn: localMidnight(cond.orderedOn), discountBp: r.discountBp, shippingCents: r.shippingCents,
      earlyPaymentBp: r.earlyPaymentBp, earlyPaymentDays: r.earlyPaymentDays, lines,
    });
  }
  // ⚠️ LE BROUILLON D'UNE MARQUE RETENUE N'EST PAS VIDÉ : il n'est simplement pas parti, et le retirer
  //    effacerait la saisie que l'éditeur garde sous les yeux de l'opérateur.
  const kept = new Set([...groups.map((g) => g.draftNumber).filter((x): x is string => !!x), ...heldDrafts]);
  return { groups, discardDrafts: loadedDrafts.filter((d) => !kept.has(d.number)), problems, held };
}

/**
 * Le prix pré-rempli d'un article qui entre dans la saisie.
 * ⚠️ UN BL NE REPREND PAS LE DERNIER PRIX DE COMMANDE : il est BRUT, et un BL reçoit un prix net. Il part du prix de
 *    la fiche ; celui de la commande qu'il solde lui vient par « Une commande ouverte », déjà net de la remise.
 */
export const prefilledPrice = (m: OrderModel, kind: EditorKind): string =>
  inputFromFine(kind === 'order' ? m.lastPrice?.unitPriceFine ?? null : m.sheetPrice);

/**
 * LE LOT DE BL À ENVOYER — un BL par fournisseur qui porte au moins une pièce, à la quantité REÇUE.
 * ⚠️ UNE MARQUE SANS FOURNISSEUR NE S'INTÈGRE PAS : elle reste dans l'éditeur à l'intégration (une entrée d'achat
 *    sans fournisseur serait un achat que personne n'a vendu) ; enregistrée, elle part.
 * ⚠️ LA COMMANDE RATTACHÉE suit son fournisseur : `links` est rangé par groupe.
 */
export function deliveryBatchOf(
  cards: readonly EditorCard[], conds: Readonly<Record<string, EditorConditions>>, links: Readonly<Record<string, string>>,
  taken: ReadonlySet<string>, today: string, mode: 'save' | 'integrate',
): { groups: DeliveryGroupInput[]; problems: BatchProblem[]; held: string[] } {
  const problems: BatchProblem[] = [];
  const groups: DeliveryGroupInput[] = [];
  const held: string[] = [];
  const numbers = numbersOf(filledGroups(cards), conds, taken, today, 'deliveryNote');
  for (const g of groupsOf(cards)) {
    const lines = linesOf(cards, g.cards);
    if (lines.length === 0) continue;
    if (g.orphanBrand && mode === 'integrate') { held.push(g.supplierLabel); continue; }
    for (const i of g.cards) {
      const c = cards[i]!;
      if (cardPieces(c) > 0 && fineFromInput(c.pa) === undefined) problems.push({ kind: 'price', model: c.model.name });
    }
    const num = numbers.get(g.key)!;
    if (num.problem) problems.push({ kind: 'number', supplier: g.supplierLabel, problem: num.problem });
    const day = (conds[g.key] ?? conditionsFromTerms(null, null, null, today)).orderedOn;
    const dateProblem = orderDateProblem(day, today);
    if (dateProblem) problems.push({ kind: 'date', supplier: g.supplierLabel, problem: dateProblem });
    groups.push({
      number: num.number, deliveredOn: localMidnight(day), supplierId: g.supplierId ?? '', supplierLabel: g.supplierLabel,
      ...(g.orphanBrand ? { brandId: g.orphanBrand.id } : {}),
      ...(links[g.key] ? { orderRef: links[g.key] } : {}),
      lines: lines.map((l) => ({ gencode: l.gencode, qty: l.orderedQty, unitPriceFine: l.unitPriceFine })),
    });
  }
  return { groups, problems, held };
}

/**
 * Ajoute des quantités à la saisie : `add` les cumule (dupliquer une commande, reprendre un reste à livrer), `set`
 * les remplace pour les articles concernés (réassort). Un modèle absent de la saisie y entre, au prix donné — ou
 * au prix pré-rempli du document.
 */
export function mergeQuantities(
  cards: readonly EditorCard[], incoming: readonly { model: OrderModel; qty: Record<string, number>; pa?: string }[], mode: 'add' | 'set',
  kind: EditorKind = 'order',
): EditorCard[] {
  const out = cards.map((c) => ({ ...c, qty: { ...c.qty } }));
  for (const inc of incoming) {
    const qty = orderableQty(inc.model, inc.qty).qty;
    const at = out.findIndex((c) => c.model.modelKey === inc.model.modelKey);
    if (at < 0) {
      out.push({ model: inc.model, pa: inc.pa ?? prefilledPrice(inc.model, kind), qty });
      continue;
    }
    const c = out[at]!;
    if (mode === 'set') c.qty = qty;
    else for (const [g, n] of Object.entries(qty)) c.qty[g] = (c.qty[g] ?? 0) + n;
  }
  return out;
}
